import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, RootState } from '../../store/store';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import ApplyControllerSetupModal from '../../components/ApplyControllerSetupModal/ApplyControllerSetupModal';
import { StatusItemsModal } from '../EditController/SubPages/FaultControl/StatusItemsModal';
import { DEFAULT_INPUT_CLASS } from '../../components/InputControl/constants';
import { ButtonType } from '../../types/button-type';
import { CONTROLLER_SETTINGS_LIMITS } from '../EditController/SubPages/Settings/settingsActions';
import { POWER_SHARE_CURRENT_LIMIT } from '../EditController/SubPages/PowerShare/powerShareActions';
import { CHANNEL_SETTINGS_LIMITS } from '../EditController/SubPages/EditChannel/channelActions';
import { IControllerSetupChannelConfig, IControllerSetupConfig } from '../../types/controller-setup-types';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const numberOrNull = (value: string) => (value === '' ? null : Number(value));

const EditControllerSetup: React.FC<ComponentProps> = ({ user, getControllerSetup, updateControllerSetup }) => {
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [config, setConfig] = useState<IControllerSetupConfig | null>(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [openFaultSetupIdx, setOpenFaultSetupIdx] = useState<number | null>(null);

  const isTecheat = !!user?.organization?.isTecheat;

  useEffect(() => {
    (async () => {
      const setup = await getControllerSetup(id);
      if (setup) {
        setName(setup.name);
        setConfig(setup.config);
      }
      setIsLoading(false);
    })();
  }, [id]);

  const updateField = <K extends keyof IControllerSetupConfig>(field: K, value: IControllerSetupConfig[K]) => {
    setConfig((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const updateChannelField = <K extends keyof IControllerSetupChannelConfig>(
    channelNumber: number,
    field: K,
    value: IControllerSetupChannelConfig[K],
  ) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        channels: prev.channels.map((channel) =>
          channel.channelNumber === channelNumber ? { ...channel, [field]: value } : channel,
        ),
      };
    });
  };

  const updateFaultItem = (setupIdx: number, itemIdx: number, value: boolean) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        faultControl: prev.faultControl.map((setup, idx) =>
          idx !== setupIdx
            ? setup
            : { ...setup, items: setup.items.map((item, iIdx) => (iIdx === itemIdx ? { ...item, status: value } : item)) },
        ),
      };
    });
  };

  const onSaveHandler = async () => {
    if (!config) return;
    setIsSaving(true);
    await updateControllerSetup({ id, name, config });
    setIsSaving(false);
  };

  if (isLoading || !config) {
    return (
      <div className="p-edit-controller-setup pb-4 md:pb-25">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex h-full w-full items-center justify-center py-10">
            <SpinnerLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-edit-controller-setup pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke px-7 py-4 dark:border-strokedark">
          <input
            type="text"
            className={`${DEFAULT_INPUT_CLASS} max-w-xs`}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <div className="flex gap-3">
            <InteractiveButton
              text="Apply to Controller(s)"
              type={ButtonType.PrimaryInverse}
              onClick={() => setShowApplyModal(true)}
            />
            <InteractiveButton text={isSaving ? 'Saving...' : 'Save'} onClick={onSaveHandler} isLoading={isSaving} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 p-7 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-black dark:text-white">Cooling Spread Control</label>
            <input
              type="number"
              className={DEFAULT_INPUT_CLASS}
              min={CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MIN}
              max={CONTROLLER_SETTINGS_LIMITS.COOLING_SPREAD_CONTROL.MAX}
              value={config.cooling_spread_control ?? ''}
              onChange={(event) => updateField('cooling_spread_control', numberOrNull(event.target.value))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-black dark:text-white">PID P</label>
            <input
              type="number"
              className={DEFAULT_INPUT_CLASS}
              min={CONTROLLER_SETTINGS_LIMITS.PID_P.MIN}
              max={CONTROLLER_SETTINGS_LIMITS.PID_P.MAX}
              value={config.pid_p ?? ''}
              onChange={(event) => updateField('pid_p', numberOrNull(event.target.value))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-black dark:text-white">PID I</label>
            <input
              type="number"
              className={DEFAULT_INPUT_CLASS}
              min={CONTROLLER_SETTINGS_LIMITS.PID_I.MIN}
              max={CONTROLLER_SETTINGS_LIMITS.PID_I.MAX}
              value={config.pid_i ?? ''}
              onChange={(event) => updateField('pid_i', numberOrNull(event.target.value))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-black dark:text-white">PID D</label>
            <input
              type="number"
              className={DEFAULT_INPUT_CLASS}
              min={CONTROLLER_SETTINGS_LIMITS.PID_D.MIN}
              max={CONTROLLER_SETTINGS_LIMITS.PID_D.MAX}
              value={config.pid_d ?? ''}
              onChange={(event) => updateField('pid_d', numberOrNull(event.target.value))}
            />
          </div>
        </div>

        {isTecheat && (
          <div className="border-t border-stroke px-7 py-4 dark:border-strokedark">
            <h3 className="mb-3 font-medium text-black dark:text-white">Power Share (Techeat only)</h3>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={!!config.is_power_share_enabled}
                  onChange={(event) => updateField('is_power_share_enabled', event.target.checked)}
                />
                Power Sharing Enabled
              </label>
              <div>
                <label className="mb-1 block text-sm font-medium text-black dark:text-white">Power Share Amps</label>
                <input
                  type="number"
                  className={DEFAULT_INPUT_CLASS}
                  min={POWER_SHARE_CURRENT_LIMIT.MIN}
                  max={POWER_SHARE_CURRENT_LIMIT.MAX}
                  value={config.power_share_current_limit ?? ''}
                  onChange={(event) => updateField('power_share_current_limit', numberOrNull(event.target.value))}
                />
              </div>
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={!!config.is_allowed_to_run_at_100}
                  onChange={(event) => updateField('is_allowed_to_run_at_100', event.target.checked)}
                />
                Full Power Allowed
              </label>
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={!!config.preheat_run_if_faulted}
                  onChange={(event) => updateField('preheat_run_if_faulted', event.target.checked)}
                />
                Pre-Heat Run If Faulted
              </label>
            </div>
          </div>
        )}

        <div className="border-t border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="mb-3 font-medium text-black dark:text-white">Fault Control</h3>
          <div className="flex flex-wrap gap-3">
            {config.faultControl.map((setup, idx) => (
              <InteractiveButton
                key={setup.command}
                text={setup.name}
                // Mirrors FaultControl.tsx / the backend's own FaultControlSetupPresenter:
                // a setup's "enabled" state is its first item's status (the master toggle),
                // recomputed here so it stays in sync with edits made in the modal below.
                type={setup.items[0]?.status ? ButtonType.Primary : ButtonType.PrimaryInverse}
                onClick={() => setOpenFaultSetupIdx(idx)}
              />
            ))}
          </div>
          {openFaultSetupIdx !== null && (
            <StatusItemsModal
              isVisible={openFaultSetupIdx !== null}
              modalTitle={config.faultControl[openFaultSetupIdx].name}
              onClose={() => setOpenFaultSetupIdx(null)}
              onSubmit={(itemIdx, value) => updateFaultItem(openFaultSetupIdx, itemIdx, value)}
              items={config.faultControl[openFaultSetupIdx].items}
            />
          )}
        </div>

        <div className="border-t border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="mb-3 font-medium text-black dark:text-white">Channels</h3>
          <div className="max-h-[520px] overflow-y-auto rounded border border-stroke dark:border-strokedark">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-primary text-left text-xs uppercase leading-5 text-white">
                  <th className="py-1.5 pl-4">Channel</th>
                  <th className="py-1.5">Name</th>
                  <th className="py-1.5">Shunt</th>
                  <th className="py-1.5">Machine</th>
                  <th className="py-1.5">Phase</th>
                  <th className="py-1.5">Current Scaling</th>
                  <th className="py-1.5">Max Temp</th>
                  <th className="py-1.5">Trim Setpoint</th>
                  <th className="py-1.5 pr-4">Power Scale</th>
                </tr>
              </thead>
              <tbody>
                {config.channels.map((channel, idx) => (
                  <tr
                    key={channel.channelNumber}
                    className={`border-b border-stroke text-xs dark:border-strokedark ${
                      idx % 2 === 0 ? 'bg-white dark:bg-boxdark' : 'bg-gray-2 dark:bg-meta-4'
                    }`}
                  >
                    <td className="py-1 pl-4 font-medium text-black dark:text-white">{channel.channelNumber}</td>
                    <td className="py-1 pr-1">
                      <input
                        type="text"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        value={channel.name ?? ''}
                        onChange={(event) => updateChannelField(channel.channelNumber, 'name', event.target.value)}
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.SHUNT_NUMBER.MAX}
                        value={channel.shunt_number ?? ''}
                        onChange={(event) =>
                          updateChannelField(channel.channelNumber, 'shunt_number', numberOrNull(event.target.value))
                        }
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.MACHINE_NUMBER.MAX}
                        value={channel.machine_number ?? ''}
                        onChange={(event) =>
                          updateChannelField(channel.channelNumber, 'machine_number', numberOrNull(event.target.value))
                        }
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.PHASE_NUMBER.MAX}
                        value={channel.phase_number ?? ''}
                        onChange={(event) =>
                          updateChannelField(channel.channelNumber, 'phase_number', numberOrNull(event.target.value))
                        }
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.CURRENT_SCALING.MAX}
                        value={channel.current_scaling ?? ''}
                        onChange={(event) =>
                          updateChannelField(channel.channelNumber, 'current_scaling', numberOrNull(event.target.value))
                        }
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.MAXIMUM_CHANNEL_TEMPERATURE.MAX}
                        value={channel.maximum_channel_temperature ?? ''}
                        onChange={(event) =>
                          updateChannelField(
                            channel.channelNumber,
                            'maximum_channel_temperature',
                            numberOrNull(event.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="py-1 pr-1">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.TRIM_CHANNEL_SETPOINT.MAX}
                        value={channel.trim_channel_setpoint ?? ''}
                        onChange={(event) =>
                          updateChannelField(
                            channel.channelNumber,
                            'trim_channel_setpoint',
                            numberOrNull(event.target.value),
                          )
                        }
                      />
                    </td>
                    <td className="py-1 pr-4">
                      <input
                        type="number"
                        className={`${DEFAULT_INPUT_CLASS} !h-7 !py-1 !text-xs`}
                        min={CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MIN}
                        max={CHANNEL_SETTINGS_LIMITS.POWER_OUTPUT_SCALE.MAX}
                        value={channel.power_output_scale ?? ''}
                        onChange={(event) =>
                          updateChannelField(
                            channel.channelNumber,
                            'power_output_scale',
                            numberOrNull(event.target.value),
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ApplyControllerSetupModal isVisible={showApplyModal} controllerSetupId={id} onClose={() => setShowApplyModal(false)} />
    </div>
  );
};

const mapState = (state: RootState) => ({
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllerSetup: dispatch.controller_setups.getControllerSetup,
  updateControllerSetup: dispatch.controller_setups.updateControllerSetup,
});

export default connect(mapState, mapDispatch)(EditControllerSetup);
